/**
 * Skynet · sk-tabs (módulo 12)
 * Pestañas accesibles (patrón WAI-ARIA): clic o flechas izquierda/derecha, Inicio y Fin.
 * Los botones se crean aquí a partir de los paneles (data-title, data-icon): cada pestaña se pinta sola.
 * Sin JS la lista de pestañas sigue oculta y los paneles se ven todos con su título.
 */
class SkTabs extends HTMLElement {
  #ac = null;
  #mo = null;
  #queued = false;

  connectedCallback() {
    this.#build();
    // En el editor un bloque hijo puede cambiar sin recargar la sección: se rehacen los botones.
    const panelsBox = this.querySelector('.sk-tabs__panels');
    if (panelsBox && !this.#mo) {
      this.#mo = new MutationObserver(() => {
        if (this.#queued) return;
        this.#queued = true;
        requestAnimationFrame(() => {
          this.#queued = false;
          if (this.isConnected) this.#build();
        });
      });
      this.#mo.observe(panelsBox, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-title', 'data-icon'] });
    }
  }

  #build() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const list = this.querySelector('[data-sk-tablist]');
    const panels = [...this.querySelectorAll('[data-sk-panel]')].filter((p) => p.closest('sk-tabs') === this);
    if (!list) return;
    if (panels.length === 0) {
      list.replaceChildren();
      list.hidden = true;
      this.classList.remove('sk-tabs--ready');
      return;
    }
    const hadFocus = list.contains(document.activeElement);
    const prev = list.querySelector('[aria-selected="true"]')?.getAttribute('aria-controls');
    list.replaceChildren();
    const tabs = panels.map((panel, i) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'sk-tabs__tab';
      tab.setAttribute('role', 'tab');
      tab.id = `SkTab-${panel.id.replace(/^SkPanel-/, '')}`;
      tab.setAttribute('aria-controls', panel.id);
      if (panel.dataset.icon) {
        const icon = document.createElement('span');
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = panel.dataset.icon;
        tab.append(icon);
      }
      const label = document.createElement('span');
      label.textContent = panel.dataset.title || String(i + 1);
      tab.append(label);
      list.append(tab);
      // Los papeles de pestaña solo con JS: sin él los paneles son contenido normal con su título.
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;
      return tab;
    });
    list.hidden = false;
    this.classList.add('sk-tabs--ready');
    const keep = panels.findIndex((p) => p.id === prev);
    this.#select(tabs, keep >= 0 ? keep : 0, hadFocus);

    tabs.forEach((tab, i) => tab.addEventListener('click', () => this.#select(tabs, i, true), { signal }));
    list.addEventListener(
      'keydown',
      (e) => {
        const i = tabs.indexOf(document.activeElement);
        if (i < 0) return;
        const rtl = getComputedStyle(list).direction === 'rtl';
        let next = -1;
        if (e.key === 'ArrowRight') next = rtl ? i - 1 : i + 1;
        else if (e.key === 'ArrowLeft') next = rtl ? i + 1 : i - 1;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === -1) return;
        e.preventDefault();
        this.#select(tabs, (next + tabs.length) % tabs.length, true);
      },
      { signal }
    );
    // En el editor: al seleccionar el bloque de una pestaña, se abre esa pestaña.
    document.addEventListener(
      'shopify:block:select',
      (e) => {
        const idx = panels.findIndex((p) => p.id === `SkPanel-${e.detail?.blockId}`);
        if (idx >= 0) this.#select(tabs, idx, false);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#mo?.disconnect();
    this.#mo = null;
  }

  #select(tabs, index, focus) {
    tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') || '')}`);
      if (panel) {
        panel.hidden = !on;
        // Módulo 19: un vídeo de una pestaña que se oculta no sigue sonando.
        if (!on) panel.querySelectorAll('video').forEach((v) => v.paused || v.pause());
      }
    });
    if (focus) tabs[index]?.focus();
  }
}

if (!customElements.get('sk-tabs')) customElements.define('sk-tabs', SkTabs);
